/* eslint-disable no-param-reassign */
import React, {Fragment, useRef} from 'react';
import {FormControl, Stack, Typography} from '@mui/material';
import {useLocation} from 'react-router-dom';

import {translations} from 'presentation/translations-new';
import {TermsNamesEnum} from 'presentation/hooks';
import {AlertModal, IconIllustration} from 'presentation/components';
import {RefundEnum} from 'presentation/constants/mapRoutesEnum';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {FileUpload} from './FileUpload';
import {Container, FileInput, Label, StyledLoading} from './styles';
import {ConfirmActionModal} from './ConfirmActionModal';
import {UploadTermsModal} from './UploadTermsModal';
import {UploadFilesProps} from './types';
import {useFileHandler} from './hooks/useFileHandler';

const {
	addFiles,
	warning,
	modal: {actionModal},
	fileUpload: {loadingFile},
} = translations['pt-br'].others.uploadFiles;

const {advice} = translations['pt-br'].privateFlow.refund.uploadRefundFiles;
const {header, title, description, button} =
	translations['pt-br'].privateFlow.refund.replacedFileModal;

export function UploadRefundFiles({
	values,
	removeFile,
	onChangeFile,
	shouldShowTermsModal,
	externalError,
	isUploading,
	setExternaErrorMessage,
	errorDataTagId,
}: UploadFilesProps) {
	const {pathname} = useLocation();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const getFileTagId = (fileIndex: number) => {
		const isRequestPreview = pathname.includes(RefundEnum.REQUEST_PREVIEW);
		const isRequest = pathname.includes(RefundEnum.REQUEST) && !isRequestPreview;

		if (isRequestPreview) {
			return `${RefundTaggingEnum.ADICIONOU_ARQUIVO_PREVIA_REEMBOLSO}${fileIndex}`;
		}
		if (isRequest) {
			return `${RefundTaggingEnum.ADICIONOU_ARQUIVO}${fileIndex}`;
		}
		return `${RefundTaggingEnum.ADICIONOU_ARQUIVO}${fileIndex}`;
	};

	const {
		allowedExtensions,
		errorState,
		handleConfirmModal,
		handleDragLeave,
		handleDragOver,
		handleOnChange,
		handleOnDrop,
		handleToggleConfirmAction,
		handleToggleConfirmActionModal,
		handleUploadByClick,
		isConfirmActionModalOpenState,
		isDragAndDropState,
		isOnDropState,
		isTermsModalOpenState,
		openDuplicateModalState,
		setIsConfirmActionModalOpenState,
		setIsTermsModalOpenState,
		setOpenDuplicateModalState,
	} = useFileHandler({
		onChangeFile,
		removeFile,
		setExternaErrorMessage,
		shouldShowTermsModal,
		values,
		isUploading,
	});

	const hasError = externalError || errorState;

	const uploadedFilesList = values?.filter(file => file.uploaded || file.error);

	return (
		<Fragment>
			{uploadedFilesList.length ? (
				<Stack gap={2}>
					{uploadedFilesList.map(file => (
						<FileUpload
							dataTagId={getFileTagId(uploadedFilesList.indexOf(file) + 1)}
							key={file.id}
							success={file.uploaded}
							error={file.error}
							size={file.readableSize}
							title={file.filename}
							onRemove={() => {
								handleToggleConfirmActionModal();
								handleToggleConfirmAction(file.id);
							}}
						/>
					))}
				</Stack>
			) : null}

			<Container
				$isError={!!errorState || !!externalError}
				justifyContent="center"
				alignItems="center"
				onDrop={handleOnDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				$isOnDrop={isOnDropState}
				$isUploading={isUploading}
				spacing={1}>
				{isUploading ? (
					<StyledLoading text={loadingFile} />
				) : (
					<Fragment>
						<IconIllustration
							customIconName="upload-file-filled"
							variant={errorState || externalError ? 'error' : 'primary'}
							size="custom"
						/>
						<FormControl>
							<Label
								data-tag-id={RefundTaggingEnum.CLICK_ADICIONAR_ARQUIVOS_REEMBOLSO}
								htmlFor="file"
								as="label">
								{addFiles}
							</Label>

							<FileInput
								id="file"
								type="file"
								ref={fileInputRef}
								accept={allowedExtensions}
								onChange={e => {
									handleOnChange(e);
								}}
								onClick={handleUploadByClick}
							/>
						</FormControl>

						{externalError || errorState ? (
							<Typography variant="body2" color="error" data-tag-id={errorDataTagId}>
								{externalError || errorState}
							</Typography>
						) : null}
					</Fragment>
				)}

				<Typography
					variant="body2"
					textAlign="center"
					color={hasError ? 'error' : 'text.secondary'}>
					{isUploading ? warning : advice}
				</Typography>
			</Container>

			<AlertModal
				title={title}
				description={description}
				open={openDuplicateModalState}
				onClose={() => {
					setOpenDuplicateModalState(false);
				}}
				headerTitle={header}
				buttonLabel={button}
				variant="info"
			/>

			<ConfirmActionModal
				title={actionModal.title}
				description={actionModal.description}
				text={actionModal.text}
				onConfirm={handleConfirmModal}
				open={isConfirmActionModalOpenState}
				setOpen={setIsConfirmActionModalOpenState}
			/>

			{shouldShowTermsModal.show ? (
				<UploadTermsModal
					open={isTermsModalOpenState}
					setOpen={setIsTermsModalOpenState}
					termName={TermsNamesEnum.UPLOAD_FILES}
					fileInputRef={fileInputRef}
					isOpenedByDrandAndDrop={isDragAndDropState}
				/>
			) : null}
		</Fragment>
	);
}
